import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheckIcon, GraduationCapIcon, BookOpenIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
import { hasMatchParams, readMatchParams } from '../utils/matchParams';
interface HeroSectionProps {
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string) => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  const navigate = useNavigate();
  // Clean-URL form: /degree/:areaStudy/:program/:degreeInterest — the third
  // segment is optional.
  const {
    areaStudy,
    program,
    degreeInterest: degreeInterestParam
  } = useParams<{
    areaStudy?: string;
    program?: string;
    degreeInterest?: string;
  }>();
  // Anchor links elsewhere on the site carry the visitor's context in the URL
  // (?degreeInterest=&areaStudy=&mProgram=), so the widget opens pre-filled.
  const urlPrefill = readMatchParams();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState(hasMatchParams(urlPrefill) ? urlPrefill : undefined);
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program,
        // A query string still wins, so ?degreeInterest= can override the path.
        ...(degreeInterestParam && !formData.degreeInterest ? {
          degreeInterest: degreeInterestParam
        } : {})
      });
    }
  }, [areaStudy, program, degreeInterestParam, formData.areaOfStudy, formData.degreeInterest, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization);
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value);
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  // Resolve display labels for trust signals
  const selectedDegree = degreeInterestOptions.find(d => d.value === formData.degreeInterest);
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  }
  const heroTrustPoints = ['Flexible and Convenient', 'Accredited Schools', 'No Obligation'];
  const labelClass = 'block text-sm font-semibold text-ink mb-1.5';
  const selectClass = (hasError: boolean, disabled = false) => `block w-full h-12 pl-10 pr-3 text-sm font-medium text-ink border rounded-xl bg-background-alt appearance-none cursor-pointer outline-none transition-colors focus:bg-surface focus:ring-2 ${disabled ? 'disabled:bg-neutral-100 disabled:text-ink-muted disabled:cursor-not-allowed ' : ''}${hasError ? 'border-error focus:ring-error focus:border-error' : 'border-hairline focus:ring-primary focus:border-primary'}`;
  return <>
      {/* Hero: the copy holds the open left side of the photograph, and the
          photo runs to the right edge at full strength rather than sitting
          behind the text as a washed-out backdrop. */}
      <section className="relative bg-background-alt overflow-hidden" data-id="element-420" data-mp="8s1g9iyth7k8">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-421" data-mp="dqcoxqddlc0v">
          <div className="lg:w-[56%] lg:pr-10 py-12 md:py-16 lg:py-24 text-center lg:text-left" data-id="element-422" data-mp="do9qchy5qakj">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EDF7E7] ring-1 ring-inset ring-[#C9E6B8] text-sm font-semibold mb-5 text-[#3F7F22]" data-id="element-423" data-mp="64r1vw659p4k">
              <span className="flex h-2 w-2 rounded-full bg-accent" data-id="element-424" data-mp="3scqlowij5d3n" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-ink tracking-tight mb-5 leading-[1.15]" data-id="element-425" data-mp="3nh1jsmj1fk46">
              {headline}
            </h1>

            <p className="text-base md:text-lg text-ink-secondary mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-426" data-mp="3el29yuc70qn7">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <ul className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm font-medium text-ink-secondary" data-id="element-427" data-mp="2zo300ztvclpk">
              {heroTrustPoints.map(point => <li key={point} className="flex items-center gap-2" data-id="element-428" data-mp="nfm8xb1cq87k">
                  <CheckIcon className="w-4 h-4 flex-shrink-0 text-accent" strokeWidth={3} aria-hidden="true" data-id="element-429" data-mp="sefrhhvhwpel" />
                  {point}
                </li>)}
            </ul>
          </div>
        </div>

        {/* On mobile the photo follows the copy as its own band; from lg it
            fills the right side of the hero at full height. */}
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-[46%]" data-id="element-430" data-mp="9gmnsdd1sbg5">
          <img src="https://cdn.magicpatterns.com/uploads/mUaxjLxnPwBJa8FwQnbTuQ/Hero_Background_FOCC.png" alt="A student studying on her laptop at the kitchen table beside her son" className="h-56 w-full object-cover object-[72%_100%] sm:h-72 lg:h-full" data-id="element-431" data-mp="3qx4hjkoy7j91" />
        </div>
      </section>

      {/* Search: a single full-width row directly under the hero, so the three
          choices and the action read as one step. */}
      <section aria-labelledby="school-search-heading" className="bg-surface border-b border-hairline shadow-ds" data-id="element-432" data-mp="2y9lbz6v4fh75">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-433" data-mp="3c9w0i38ykjp5">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between mb-5" data-id="element-434" data-mp="379qeb2lgjpeg">
            <h2 id="school-search-heading" className="text-lg font-bold text-ink" data-id="element-435" data-mp="3m68smwouuphs">
              Search featured schools
            </h2>
            <p className="flex items-center gap-2 text-xs font-semibold text-ink-secondary" data-id="element-436" data-mp="3v03yy81pqlz0">
              <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 text-accent" aria-hidden="true" data-id="element-437" data-mp="3qy91xfqteur1" />
              Trusted by 2M+ students to find the right school
            </p>
          </div>

          <form onSubmit={handleContinue} className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto] lg:gap-5 lg:items-end" data-id="element-438" data-mp="3um152wgdsl2">
            {/* Degree Level */}
            <div data-id="element-439" data-mp="2m0twe9qzjddd">
              <label htmlFor="degreeInterest" className={labelClass} data-id="element-440" data-mp="2m0buawhn64xj">
                Degree level
              </label>
              <div className="relative" data-id="element-441" data-mp="2hy7304eo1ke2">
                <GraduationCapIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" aria-hidden="true" data-id="element-442" data-mp="2d1zsrzf8lmqb" />
                <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} className={selectClass(Boolean(errors.degreeInterest))} data-id="element-443" data-mp="35pm8u75niju2">
                  <option value="" data-id="element-444" data-mp="1pc5h3l6nm47n">Select degree level</option>
                  {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-445" data-mp="1kbhr92u5vtf0">
                      {opt.type}
                    </option>)}
                </select>
              </div>
              {errors.degreeInterest && <p className="mt-1.5 text-xs text-error" data-id="element-446" data-mp="1f7yssjtvb2bn">
                  {errors.degreeInterest}
                </p>}
            </div>

            {/* Area of Study */}
            <div data-id="element-447" data-mp="1oz1v1oyj9nci">
              <label htmlFor="areaOfStudy" className={labelClass} data-id="element-448" data-mp="1ozlcl4k9u9q0">
                Area of study
              </label>
              <div className="relative" data-id="element-449" data-mp="1xy6sst29tcnm">
                <BookOpenIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" aria-hidden="true" data-id="element-450" data-mp="1xx5iwrwzqw7h" />
                <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={selectClass(Boolean(errors.areaOfStudy))} data-id="element-451" data-mp="27vbi31rexo8n">
                  <option value="" data-id="element-452" data-mp="5he2ozf7iiql">Select area of study</option>
                  {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-453" data-mp="jcha7ub67by">
                      {opt.type}
                    </option>)}
                </select>
              </div>
              {errors.areaOfStudy && <p className="mt-1.5 text-xs text-error" data-id="element-454" data-mp="af5r3jgn7mq1">
                  {errors.areaOfStudy}
                </p>}
            </div>

            {/* Specialization */}
            <div data-id="element-455" data-mp="jgdl3pc4m1eb">
              <label htmlFor="specialization" className={labelClass} data-id="element-456" data-mp="jgvqbbgrnpyd">
                Program
              </label>
              <div className="relative" data-id="element-457" data-mp="y8e2szaf32cz">
                <SearchIcon className={`pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${formData.areaOfStudy ? 'text-primary' : 'text-ink-muted'}`} aria-hidden="true" data-id="element-458" data-mp="t6ov9tmel6tn" />
                <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} className={selectClass(Boolean(errors.specialization), true)} data-id="element-459" data-mp="1378xayhqyfcx">
                  <option value="" data-id="element-460" data-mp="1hqa423edoa5n">
                    {formData.areaOfStudy ? 'Select program' : 'Select an area of study first'}
                  </option>
                  {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-461" data-mp="u3k212214fl1">
                      {opt.type}
                    </option>)}
                </select>
              </div>
              {errors.specialization && <p className="mt-1.5 text-xs text-error" data-id="element-462" data-mp="k54ipl98uam3">
                  {errors.specialization}
                </p>}
            </div>

            <button type="submit" className="w-full lg:w-auto inline-flex items-center justify-center h-12 px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" style={{
            backgroundColor: '#5cb335',
            backgroundImage: 'none'
          }} data-id="element-463" data-mp="1rh3mliegxqau">
              Find My Program
            </button>
          </form>
        </div>
      </section>
    </>;
}